// Reusable read-only block that renders the "Informations du destinataire"
// (client_payload) attached to an order. Used in 3 places:
//   1. Affiliate "validated-orders" view (App.js) — affiliate must read these to deliver.
//   2. Admin "pending-orders" / orders view (App.js) — admin must approve.
//   3. Client "Mes commandes" view (ClientDashboard.js) — client confirms what they entered.
//
// Each row exposes a Copy button so the affiliate can paste into Free Fire / Wise / etc.
import React from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, testid }) => {
  if (value === undefined || value === null || value === "") return null;
  const onCopy = (e) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(String(value));
      toast.success("Copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.4rem",
        padding: "0.3rem 0.45rem",
        background: "#FFF",
        borderRadius: 6,
        border: "1px solid #E2E8F0",
        marginBottom: 4,
      }}
      data-testid={testid}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: "0.62rem", color: "#64748B", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {label}
        </div>
        <div style={{ fontSize: "0.84rem", fontWeight: 700, color: "#0F172A", wordBreak: "break-all" }}>
          {String(value)}
        </div>
      </div>
      <button
        onClick={onCopy}
        style={{
          background: "#2563EB",
          color: "white",
          border: "none",
          borderRadius: 6,
          padding: "0.2rem 0.45rem",
          cursor: "pointer",
          fontSize: "0.65rem",
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <Copy size={11} /> Copier
      </button>
    </div>
  );
};

/**
 * Render the recipient info block.
 * @param {Object} payload  client_payload object stored on the order
 * @param {String} testidPrefix  prefix used for data-testid attributes (default "recipient")
 */
export default function RecipientInfo({ payload, testidPrefix = "recipient" }) {
  if (!payload || typeof payload !== "object" || Object.keys(payload).length === 0) return null;
  const t = payload.type || "";
  const hasAnyValue = Object.entries(payload).some(([k, v]) => k !== "type" && v !== "" && v !== null && v !== undefined);
  if (!hasAnyValue) return null;

  // Friendly label for the recipient kind.
  const kindLabel = ({
    freefire: "Compte du jeu",
    efootball: "Compte eFootball",
    boost: "Boostage Réseau Social",
    wise: "Compte Wise",
    giftcard_email: "Email Gift Card",
    email: "Email destinataire",
    crypto: "Adresse crypto",
    free_text: "Détails pour l'administrateur",
    wallet: "Adresse de wallet crypto",
    account_id: "Identifiant du compte",
  })[t] || "Informations destinataire";

  return (
    <div
      data-testid={`${testidPrefix}-block`}
      style={{
        marginTop: "0.4rem",
        padding: "0.55rem",
        background: "#FFFBEB",
        borderRadius: 8,
        border: "1px solid #FCD34D",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
        <Check size={13} style={{ color: "#92400E" }} />
        <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {kindLabel}
        </span>
      </div>

      {t === "freefire" && (
        <>
          <Field label="Nom du joueur" value={payload.player_name} testid={`${testidPrefix}-player-name`} />
          <Field label="ID du compte" value={payload.game_id} testid={`${testidPrefix}-game-id`} />
          {payload.password && <Field label="Mot de passe" value={payload.password} testid={`${testidPrefix}-password`} />}
        </>
      )}

      {t === "efootball" && (
        <>
          <Field label="Nom du joueur" value={payload.player_name} testid={`${testidPrefix}-player-name`} />
          <Field label="ID eFootball" value={payload.game_id} testid={`${testidPrefix}-game-id`} />
          <Field label="Mot de passe" value={payload.password} testid={`${testidPrefix}-password`} />
        </>
      )}

      {t === "boost" && (
        <>
          <Field label="Plateforme" value={(payload.platform || "").toUpperCase()} testid={`${testidPrefix}-platform`} />
          <Field label="Lien du post" value={payload.post_link} testid={`${testidPrefix}-post-link`} />
          <Field label="Budget / jour" value={payload.budget_usd_per_day ? `${payload.budget_usd_per_day} $` : null} testid={`${testidPrefix}-budget`} />
          <Field label="Durée" value={payload.days ? `${payload.days} jour(s)` : null} testid={`${testidPrefix}-days`} />
          <Field label="Montant total" value={payload.computed_price_htg ? `${payload.computed_price_htg} G` : null} testid={`${testidPrefix}-total`} />
        </>
      )}

      {t === "wise" && (
        <>
          <Field label="Email Wise" value={payload.email} testid={`${testidPrefix}-email`} />
          <Field label="Nom du bénéficiaire" value={payload.beneficiary_name} testid={`${testidPrefix}-beneficiary-name`} />
          <Field label="Wise Tag" value={payload.wise_tag} testid={`${testidPrefix}-wise-tag`} />
        </>
      )}

      {t === "giftcard_email" && (
        <Field label="Email gift card" value={payload.email} testid={`${testidPrefix}-giftcard-email`} />
      )}

      {t === "email" && payload.beneficiary_name !== undefined && (
        <>
          <Field label="Email destinataire" value={payload.email} testid={`${testidPrefix}-email`} />
          <Field label="Nom du bénéficiaire" value={payload.beneficiary_name} testid={`${testidPrefix}-beneficiary-name`} />
        </>
      )}

      {/* Generic admin-controlled recipient kinds. Single `value` field. */}
      {["free_text", "wallet", "account_id"].includes(t) && (
        <Field
          label={
            t === "wallet" ? "Adresse wallet" :
            t === "account_id" ? "Identifiant du compte" :
            "Détails"
          }
          value={payload.value}
          testid={`${testidPrefix}-${t}`}
        />
      )}
      {t === "email" && payload.beneficiary_name === undefined && (
        <Field label="Email destinataire" value={payload.value || payload.email} testid={`${testidPrefix}-email`} />
      )}

      {t === "crypto" && (
        <>
          <Field label="Type de crypto" value={payload.crypto_type} testid={`${testidPrefix}-crypto-type`} />
          <Field label="Réseau" value={payload.network} testid={`${testidPrefix}-network`} />
          <Field label="Adresse" value={payload.address} testid={`${testidPrefix}-address`} />
          <Field label="Lien (memo)" value={payload.link} testid={`${testidPrefix}-link`} />
        </>
      )}

      {/* Fallback: render any unknown keys as generic fields so nothing is ever lost. */}
      {!["freefire", "efootball", "boost", "wise", "giftcard_email", "email", "crypto", "free_text", "wallet", "account_id"].includes(t) && (
        <>
          {Object.entries(payload).map(([k, v]) =>
            k === "type" || v === "" || v === null || v === undefined ? null : (
              <Field key={k} label={k} value={v} testid={`${testidPrefix}-${k}`} />
            )
          )}
        </>
      )}
    </div>
  );
}
